import {
  Anchor,
  Badge,
  Box,
  Button,
  Card,
  Container,
  Group,
  Select,
  SimpleGrid,
  Stack,
  Text,
  TextInput,
  ThemeIcon,
  Title,
  UnstyledButton,
} from '@mantine/core'
import { DateTimePicker } from '@mantine/dates'
import { notifications } from '@mantine/notifications'
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router'
import dayjs from 'dayjs'
import { useMemo, useState } from 'react'
import { LuAnchor, LuArrowLeft, LuCheck } from 'react-icons/lu'
import { KindBadge } from '@/frontend/components/marina/KindBadge'
import { useBoats } from '@/frontend/hooks/useBoats'
import { useCreateBooking } from '@/frontend/hooks/useBookings'
import { useCustomers } from '@/frontend/hooks/useCustomers'

const FT_PER_METER = 3.28084

const CHANNELS: { value: string; label: string }[] = [
  { value: 'WALK_IN', label: 'Walk-in' },
  { value: 'PHONE', label: 'Telepon' },
  { value: 'EMAIL', label: 'Email' },
  { value: 'TEXT_MESSAGE', label: 'Pesan' },
  { value: 'RADIO', label: 'Radio' },
  { value: 'AGENT', label: 'Agen' },
  { value: 'OTHER', label: 'Lainnya' },
]

export const Route = createFileRoute('/dashboard/bookings/new')({
  component: NewBookingPage,
})

function NewBookingPage() {
  const navigate = useNavigate()
  const create = useCreateBooking()

  const [customerId, setCustomerId] = useState<string | null>(null)
  const [boatId, setBoatId] = useState<string | null>(null)
  const [checkIn, setCheckIn] = useState<Date | null>(null)
  const [checkOut, setCheckOut] = useState<Date | null>(null)
  const [channel, setChannel] = useState<string | null>(null)
  const [channelLocation, setChannelLocation] = useState('Marina Office')

  const { data: customersData } = useCustomers({ pageSize: 200 })
  const customers = customersData?.items ?? []
  const selectedCustomer = customers.find((c) => c.id === customerId)

  const { data: boatsData } = useBoats({ customerId: customerId ?? undefined, pageSize: 100 })
  const allBoats = boatsData?.items ?? []
  const selectedBoat = allBoats.find((b) => b.id === boatId)

  const customerOptions = useMemo(
    () =>
      customers.map((c) => ({
        value: c.id,
        label: `${c.firstName} ${c.lastName}${c.email ? ` · ${c.email}` : ''}`,
      })),
    [customers],
  )

  const boatOptions = useMemo(
    () =>
      allBoats.map((b) => ({
        value: b.id,
        label: b.name,
      })),
    [allBoats],
  )

  // Duration
  const durationDays = checkIn && checkOut ? dayjs(checkOut).diff(dayjs(checkIn), 'day') : null
  const durationHours = checkIn && checkOut ? dayjs(checkOut).diff(dayjs(checkIn), 'hour') % 24 : null
  const invalidDates = checkIn && checkOut && dayjs(checkOut).isBefore(dayjs(checkIn))

  const canSubmit = !!customerId && !!boatId && !!checkIn && !!checkOut && !!channel && !invalidDates

  async function submit() {
    if (!canSubmit) return
    try {
      const { booking } = await create.mutateAsync({
        customerId: customerId!,
        boatId: boatId!,
        checkInAt: checkIn!.toISOString(),
        checkOutAt: checkOut!.toISOString(),
        channel: channel as any,
        channelLocation: channelLocation.trim() || 'Marina Office',
      })
      notifications.show({
        title: 'Permintaan dibuat',
        message: `${booking.code} berhasil dibuat — status Pending.`,
        color: 'teal',
      })
      navigate({ to: '/dashboard/reservations/requests' as any })
    } catch (e: unknown) {
      notifications.show({
        title: 'Gagal membuat booking',
        message: e instanceof Error ? e.message : 'Silakan coba lagi',
        color: 'red',
      })
    }
  }

  return (
    <Container size="md" py="md">
      <Stack gap="lg">
        {/* Header */}
        <Group gap="sm" align="center">
          <Anchor component={Link} to="/dashboard/reservations/requests" c="dimmed" style={{ display: 'flex' }}>
            <LuArrowLeft size={18} />
          </Anchor>
          <div>
            <Title order={2} ff="var(--font-display)" fw={600}>
              Buat Permintaan Booking
            </Title>
            <Text size="xs" c="dimmed">
              Status awal: Pending — perlu disetujui oleh manajer
            </Text>
          </div>
        </Group>

        {/* Step 1 — Pelanggan */}
        <StepCard step={1} title="Pilih Pelanggan" done={!!customerId}>
          <Select
            data={customerOptions}
            placeholder="Cari nama atau email pelanggan…"
            searchable
            clearable
            value={customerId}
            onChange={(v) => {
              setCustomerId(v)
              setBoatId(null)
            }}
          />
          {selectedCustomer && (
            <Box
              mt="xs"
              p="sm"
              style={{
                borderRadius: 8,
                background: 'light-dark(var(--mantine-color-laut-0), rgba(14,124,123,0.12))',
              }}
            >
              <Group gap="xs" wrap="nowrap">
                <ThemeIcon variant="light" color="laut" size="sm" radius="xl">
                  <LuCheck size={12} />
                </ThemeIcon>
                <div style={{ minWidth: 0, flex: 1 }}>
                  <Text size="sm" fw={600} truncate>
                    {selectedCustomer.firstName} {selectedCustomer.lastName}
                  </Text>
                  <Group gap="xs" mt={2}>
                    <KindBadge category={selectedCustomer.category} />
                    {selectedCustomer.email && (
                      <Text size="xs" c="dimmed" truncate>
                        {selectedCustomer.email}
                      </Text>
                    )}
                  </Group>
                </div>
              </Group>
            </Box>
          )}
          {!customerId && (
            <Text size="xs" c="dimmed" mt={4}>
              Pelanggan belum terdaftar?{' '}
              <Anchor component={Link} to="/dashboard/accounts/customers/new" c="laut" size="xs">
                Tambah pelanggan baru →
              </Anchor>
            </Text>
          )}
        </StepCard>

        {/* Step 2 — Kapal */}
        <StepCard step={2} title="Pilih Kapal" done={!!boatId} disabled={!customerId}>
          <Select
            data={boatOptions}
            placeholder={customerId ? 'Pilih kapal pelanggan ini…' : 'Pilih pelanggan dulu'}
            searchable
            clearable
            disabled={!customerId}
            value={boatId}
            onChange={setBoatId}
          />
          {selectedBoat && (
            <Box
              mt="xs"
              p="sm"
              style={{
                borderRadius: 8,
                background: 'light-dark(var(--mantine-color-laut-0), rgba(14,124,123,0.12))',
              }}
            >
              <Group gap="xs" wrap="nowrap">
                <ThemeIcon variant="light" color="laut" size="sm" radius="xl">
                  <LuCheck size={12} />
                </ThemeIcon>
                <div style={{ minWidth: 0, flex: 1 }}>
                  <Text size="sm" fw={600}>
                    {selectedBoat.name}
                  </Text>
                  <Group gap="xs" mt={2} wrap="wrap">
                    <Badge size="xs" variant="light" color="gray">
                      {selectedBoat.type.replace(/_/g, ' ')}
                    </Badge>
                    <Text size="xs" c="dimmed">
                      LOA {(Number(selectedBoat.loaMeters) * FT_PER_METER).toFixed(1)} ft · Beam{' '}
                      {(Number(selectedBoat.beamMeters) * FT_PER_METER).toFixed(1)} ft
                    </Text>
                    {selectedBoat.flagState && (
                      <Text size="xs" c="dimmed">
                        {selectedBoat.flagState}
                      </Text>
                    )}
                  </Group>
                </div>
              </Group>
            </Box>
          )}
          {customerId && allBoats.length === 0 && (
            <Text size="xs" c="dimmed" mt={4}>
              Pelanggan ini belum punya kapal.{' '}
              <Anchor component={Link} to={`/dashboard/accounts/customers/${customerId}` as any} c="laut" size="xs">
                Tambah kapal →
              </Anchor>
            </Text>
          )}
        </StepCard>

        {/* Step 3 — Tanggal & Waktu */}
        <StepCard step={3} title="Tanggal & Waktu" done={!!checkIn && !!checkOut && !invalidDates} disabled={!boatId}>
          <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="sm">
            <DateTimePicker
              label="Check-in"
              placeholder="Pilih tanggal & waktu"
              clearable
              value={checkIn}
              onChange={(v) => setCheckIn(v ? new Date(v as any) : null)}
              valueFormat="DD MMM YYYY · HH:mm"
              disabled={!boatId}
            />
            <DateTimePicker
              label="Check-out"
              placeholder="Pilih tanggal & waktu"
              clearable
              value={checkOut}
              onChange={(v) => setCheckOut(v ? new Date(v as any) : null)}
              valueFormat="DD MMM YYYY · HH:mm"
              minDate={checkIn ?? undefined}
              disabled={!boatId}
            />
          </SimpleGrid>

          {invalidDates && (
            <Text c="red.6" size="xs" mt="xs" fw={500}>
              ⚠ Tanggal check-out harus setelah check-in
            </Text>
          )}
          {checkIn && checkOut && !invalidDates && durationDays !== null && (
            <Group gap="xs" mt="xs">
              <ThemeIcon variant="light" color="laut" size="xs" radius="xl">
                <LuCheck size={10} />
              </ThemeIcon>
              <Text size="xs" c="laut.7" fw={500}>
                Durasi: {durationDays > 0 ? `${durationDays} hari` : ''}
                {durationHours && durationHours > 0 ? ` ${durationHours} jam` : ''}
                {durationDays === 0 && durationHours === 0 ? 'Kurang dari 1 jam' : ''}
              </Text>
            </Group>
          )}
        </StepCard>

        {/* Step 4 — Saluran */}
        <StepCard step={4} title="Saluran Booking" done={!!channel}>
          {/* Channel pill buttons */}
          <Group gap="xs" wrap="wrap">
            {CHANNELS.map((ch) => {
              const active = channel === ch.value
              return (
                <UnstyledButton
                  key={ch.value}
                  onClick={() => setChannel(active ? null : ch.value)}
                  style={{
                    padding: '6px 14px',
                    borderRadius: 999,
                    fontSize: '0.825rem',
                    fontWeight: active ? 600 : 500,
                    background: active ? 'var(--mantine-color-laut-6)' : 'var(--mantine-color-gray-1)',
                    color: active ? 'white' : 'var(--mantine-color-gray-7)',
                    transition: 'background 120ms ease',
                  }}
                >
                  {ch.label}
                </UnstyledButton>
              )
            })}
          </Group>

          <TextInput
            label="Lokasi / Keterangan"
            description="Contoh: Marina Office, Loket walk-in, Agen Bali"
            placeholder="Marina Office"
            value={channelLocation}
            onChange={(e) => setChannelLocation(e.currentTarget.value)}
            mt="sm"
          />
        </StepCard>

        {/* Actions */}
        <Card radius="md" padding="md">
          <Group justify="space-between" align="center" wrap="wrap" gap="sm">
            <Stack gap={2}>
              <Text size="sm" fw={600}>
                {canSubmit ? 'Siap membuat permintaan' : 'Lengkapi semua field yang diperlukan'}
              </Text>
              <Text size="xs" c="dimmed">
                {!customerId
                  ? 'Pilih pelanggan'
                  : !boatId
                    ? 'Pilih kapal'
                    : !checkIn || !checkOut
                      ? 'Tentukan tanggal check-in & check-out'
                      : !channel
                        ? 'Pilih saluran booking'
                        : 'Semua field telah diisi'}
              </Text>
            </Stack>
            <Group gap="sm">
              <Button variant="default" onClick={() => navigate({ to: '/dashboard/reservations/requests' as any })}>
                Batal
              </Button>
              <Button
                color="laut"
                leftSection={<LuAnchor size={15} />}
                onClick={submit}
                disabled={!canSubmit}
                loading={create.isPending}
              >
                Buat Permintaan
              </Button>
            </Group>
          </Group>
        </Card>
      </Stack>
    </Container>
  )
}

// ─── Step card ────────────────────────────────────────

function StepCard({
  step,
  title,
  done,
  disabled,
  children,
}: {
  step: number
  title: string
  done?: boolean
  disabled?: boolean
  children: React.ReactNode
}) {
  return (
    <Card radius="md" padding="lg" style={{ opacity: disabled ? 0.55 : 1, transition: 'opacity 200ms ease' }}>
      <Group gap="sm" mb="md" align="center">
        <ThemeIcon variant={done ? 'filled' : 'light'} color={done ? 'teal' : 'laut'} size="md" radius="xl">
          {done ? <LuCheck size={14} /> : <span style={{ fontSize: 13, fontWeight: 700 }}>{step}</span>}
        </ThemeIcon>
        <Text fw={700} size="sm">
          {title}
        </Text>
        {!done && (
          <Badge size="xs" color="red" variant="light" ml="auto">
            Wajib
          </Badge>
        )}
        {done && (
          <Badge size="xs" color="teal" variant="light" ml="auto">
            Selesai
          </Badge>
        )}
      </Group>
      {children}
    </Card>
  )
}
